import { formatDateTimeDisplay } from "@/lib/utils/date/date";
import { formatOriginLabel } from "@/lib/utils/origin-display";
import { getEnrollmentStatusDisplayLabel } from "@/lib/utils/enrollment-status-display";
import type { ClassEnrollmentActivity, GetEnrollmentResponse } from "@/openapi/generated";
import { EnrollmentStatus } from "@/openapi/generated";

/** Reject .NET/default sentinel dates (e.g. 0001-01-01) on legacy enrollments. */
const MIN_MEANINGFUL_ENROLLMENT_YEAR = 2000;

export function isMeaningfulEnrollmentTimestamp(date: Date): boolean {
  return (
    !Number.isNaN(date.getTime()) &&
    date.getUTCFullYear() >= MIN_MEANINGFUL_ENROLLMENT_YEAR
  );
}

export function parseEnrollmentTimestamp(raw: unknown): Date | null {
  if (raw == null || raw === "") return null;
  if (raw instanceof Date) {
    return isMeaningfulEnrollmentTimestamp(raw) ? raw : null;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const parsed = new Date(trimmed);
    return isMeaningfulEnrollmentTimestamp(parsed) ? parsed : null;
  }
  return null;
}

function normalizeActivityTimestamp(
  value: Date | null | undefined,
): Date | null {
  if (value == null) return null;
  return isMeaningfulEnrollmentTimestamp(value) ? value : null;
}

/** Best available moment for when enrollment reached its current milestone. */
export function resolveEnrollmentActivityWhen(
  enrollment: Pick<
    GetEnrollmentResponse,
    | "enrolledAt"
    | "statusChangedAt"
    | "updatedAt"
    | "droppedAt"
    | "rejectedAt"
    | "cancelledAt"
    | "completedAt"
  >,
): Date | null {
  return (
    parseEnrollmentTimestamp(enrollment.enrolledAt) ??
    parseEnrollmentTimestamp(enrollment.statusChangedAt) ??
    parseEnrollmentTimestamp(enrollment.droppedAt) ??
    parseEnrollmentTimestamp(enrollment.rejectedAt) ??
    parseEnrollmentTimestamp(enrollment.cancelledAt) ??
    parseEnrollmentTimestamp(enrollment.completedAt) ??
    (enrollment.updatedAt instanceof Date
      ? normalizeActivityTimestamp(enrollment.updatedAt)
      : null)
  );
}

export function formatEnrollmentActivityActor(
  activity: ClassEnrollmentActivity,
): string {
  const email = activity.actorEmail?.trim();
  if (email) return email;
  const actorUserId = Number(activity.actorUserId);
  if (Number.isFinite(actorUserId) && actorUserId > 0) {
    return `User #${actorUserId}`;
  }
  const modified = activity.updatedBy?.trim();
  if (modified) return modified;
  const created = activity.createdBy?.trim();
  if (created) return created;
  return "System";
}

export function formatEnrollmentActivityViaOrigin(
  origin: GetEnrollmentResponse["origin"] | string | null | undefined,
): string | null {
  if (origin == null || origin === "") return null;
  const label = formatOriginLabel(origin);
  return label ? `via ${label}` : null;
}

export function resolveEnrollmentStatusHeadline(
  enrollment: Pick<GetEnrollmentResponse, "status">,
): string {
  return getEnrollmentStatusDisplayLabel(enrollment.status) || "Enrollment";
}

function activitySortKey(activity: ClassEnrollmentActivity): number {
  return (
    activity.occurredAt?.getTime() ??
    activity.createdAt?.getTime() ??
    0
  );
}

function pickPrimaryEnrollmentActivity(
  history: ClassEnrollmentActivity[],
): ClassEnrollmentActivity | null {
  if (!history.length) return null;

  const sorted = [...history].sort(
    (a, b) => activitySortKey(a) - activitySortKey(b),
  );

  const created = sorted.find(
    (row) => row.action?.trim().toLowerCase() === "created",
  );
  if (created) return created;

  const enrolled = sorted.find(
    (row) => row.toStatus === EnrollmentStatus.Enrolled,
  );
  if (enrolled) return enrolled;

  return sorted[sorted.length - 1] ?? null;
}

export function resolveEnrollmentPerformedByLabel(
  enrollment: Pick<GetEnrollmentResponse, "activityHistory" | "origin">,
): string | null {
  const primary = pickPrimaryEnrollmentActivity(enrollment.activityHistory ?? []);
  if (primary) {
    const actor = formatEnrollmentActivityActor(primary);
    if (actor !== "System") return actor;
  }

  return formatEnrollmentActivityViaOrigin(enrollment.origin);
}

/** Roster row: staff/parent who enrolled — never origin-only “via Admin portal”. */
export function resolveRosterEnrollmentEnrolledByLabel(
  enrollment: Pick<GetEnrollmentResponse, "activityHistory">,
): string | null {
  const history = enrollment.activityHistory ?? [];
  const primary = pickPrimaryEnrollmentActivity(history);
  if (primary) {
    const actor = formatEnrollmentActivityActor(primary);
    if (actor !== "System") return actor;
  }

  for (const row of history) {
    const actor = formatEnrollmentActivityActor(row);
    if (actor !== "System") return actor;
  }

  return null;
}

export type RosterEnrollmentSummary = {
  headline: string;
  when: Date | null;
  performedBy: string | null;
};

/**
 * When the parent/member enrolled (enroll button), prefer audit activity over
 * DateOnly `enrolledAt` on the enrollment row.
 */
export function resolveParentEnrolledAt(
  enrollment: Pick<
    GetEnrollmentResponse,
    "enrollmentId" | "enrolledAt" | "statusChangedAt" | "activityHistory"
  >,
  enrolledAtByEnrollmentId?: ReadonlyMap<number, Date>,
): Date | null {
  const enrollmentId = Number(enrollment.enrollmentId);
  if (
    enrolledAtByEnrollmentId &&
    Number.isFinite(enrollmentId) &&
    enrollmentId > 0
  ) {
    const fromFeed = enrolledAtByEnrollmentId.get(enrollmentId);
    if (fromFeed && !Number.isNaN(fromFeed.getTime())) {
      return fromFeed;
    }
  }

  const history = enrollment.activityHistory ?? [];
  const primary = pickPrimaryEnrollmentActivity(history);
  const fromActivity =
    normalizeActivityTimestamp(primary?.occurredAt) ??
    normalizeActivityTimestamp(primary?.createdAt);
  if (fromActivity) return fromActivity;

  return (
    parseEnrollmentTimestamp(enrollment.enrolledAt) ??
    parseEnrollmentTimestamp(enrollment.statusChangedAt)
  );
}

export function formatParentEnrolledAtDisplay(
  enrollment: Pick<
    GetEnrollmentResponse,
    "enrollmentId" | "enrolledAt" | "statusChangedAt" | "activityHistory"
  >,
  enrolledAtByEnrollmentId?: ReadonlyMap<number, Date>,
): string | null {
  const when = resolveParentEnrolledAt(enrollment, enrolledAtByEnrollmentId);
  if (!when || !isMeaningfulEnrollmentTimestamp(when)) return null;
  return formatDateTimeDisplay(when);
}

/** Date, time, and actor for roster rows (no modal). */
export function formatRosterEnrollmentMetaLine(
  enrollment: GetEnrollmentResponse,
  enrolledAtByEnrollmentId?: ReadonlyMap<number, Date>,
): string | null {
  const history = enrollment.activityHistory ?? [];
  const summary = buildRosterEnrollmentSummary(enrollment, history);
  const when =
    resolveParentEnrolledAt(enrollment, enrolledAtByEnrollmentId) ??
    summary.when;
  const whenLabel =
    when && isMeaningfulEnrollmentTimestamp(when)
      ? formatDateTimeDisplay(when.toISOString())
      : null;
  const enrolledBy = resolveRosterEnrollmentEnrolledByLabel(enrollment);

  if (whenLabel && enrolledBy) {
    return `${whenLabel} · ${enrolledBy}`;
  }
  if (whenLabel) return whenLabel;
  return enrolledBy;
}

export function buildRosterEnrollmentSummary(
  enrollment: GetEnrollmentResponse,
  history: ClassEnrollmentActivity[],
): RosterEnrollmentSummary {
  const primary = pickPrimaryEnrollmentActivity(history);
  const when =
    normalizeActivityTimestamp(primary?.occurredAt) ??
    normalizeActivityTimestamp(primary?.createdAt) ??
    resolveEnrollmentActivityWhen(enrollment);

  return {
    headline: resolveEnrollmentStatusHeadline(enrollment),
    when,
    performedBy: resolveEnrollmentPerformedByLabel(enrollment),
  };
}

function formatEnrollmentActivityAction(action: string | undefined): string {
  if (!action?.trim()) return "Status updated";
  return action
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();
}

/** Single-line label for a status-change row (modal / compact lists). */
export function formatEnrollmentActivityStatusLabel(
  activity: ClassEnrollmentActivity,
): string {
  const from = activity.fromStatus
    ? getEnrollmentStatusDisplayLabel(activity.fromStatus)
    : null;
  const to = activity.toStatus
    ? getEnrollmentStatusDisplayLabel(activity.toStatus)
    : null;
  if (from && to) return `${from} → ${to}`;
  if (to) return to;
  return formatEnrollmentActivityAction(activity.action);
}

export function sortEnrollmentActivitiesNewestFirst(
  history: ClassEnrollmentActivity[],
): ClassEnrollmentActivity[] {
  return [...history].sort((a, b) => {
    const aTime = a.occurredAt?.getTime() ?? a.createdAt?.getTime() ?? 0;
    const bTime = b.occurredAt?.getTime() ?? b.createdAt?.getTime() ?? 0;
    return bTime - aTime;
  });
}

/** @internal exported for unit tests */
export function pickPrimaryEnrollmentActivityForTest(
  history: ClassEnrollmentActivity[],
): ClassEnrollmentActivity | null {
  return pickPrimaryEnrollmentActivity(history);
}
